import { useState, useEffect, useCallback } from 'react'
import { useQuery as useReactQuery } from '@tanstack/react-query'
import { GQL_CLIENT } from 'src/graphqlClient'
import { getFileUploadConfiguration } from '../services/FileUploadConfigService'
import { UseFileUploadConfigReturn, FileUploadConfiguration, UsageTypeMetadata, SectionMetadata } from '../types'

/**
 * Hook to fetch and manage file upload configuration from backend via GraphQL
 */
export function useFileUploadConfig(): UseFileUploadConfigReturn {
  const [configuration, setConfiguration] = useState<FileUploadConfiguration | null>(null)

  const { data, isLoading, error, refetch } = useReactQuery<FileUploadConfiguration>({
    queryKey: ['file-upload-configuration'],
    cacheTime: 5 * 60 * 1000,
    staleTime: 2 * 60 * 1000,
    queryFn: () => getFileUploadConfiguration(GQL_CLIENT),
  })

  useEffect(() => {
    if (data) {
      setConfiguration(data)
    }
  }, [data])

  /**
   * Gets usage metadata by usage type
   */
  const getUsageMetadata = useCallback((usage: string): UsageTypeMetadata | null => {
    if (!configuration) return null
    return configuration.usageTypes[usage] || null
  }, [configuration])

  /**
   * Gets section metadata by section
   */
  const getSectionMetadata = useCallback((section: string): SectionMetadata | null => {
    if (!configuration) return null
    return configuration.sections[section] || null
  }, [configuration])

  /**
   * Gets available usage types, optionally filtered by section
   */
  const getAvailableUsageTypes = useCallback((section?: string): UsageTypeMetadata[] => {
    if (!configuration) return []

    const usageTypes = Object.values(configuration.usageTypes)

    if (section) {
      return usageTypes.filter(usage => usage.section === section)
    }

    return usageTypes
  }, [configuration])

  /**
   * Refreshes configuration
   */
  const refreshConfig = useCallback(async () => {
    await refetch()
  }, [refetch])

  return {
    configuration,
    isLoading,
    error: error instanceof Error ? error.message : null,
    refreshConfig,
    getUsageMetadata,
    getSectionMetadata,
    getAvailableUsageTypes,
  }
}
